import React, { useMemo } from "react";
import { IonModal, IonContent, IonHeader, IonToolbar, IonButtons, IonButton } from "@ionic/react";
import { useLearningLoop } from "@hooks/programs/useLearningLoop";
import {
  INewDeepLinkLearningLoop,
  INewTimelineEvidence,
  IUser,
  ParapassPDFRequestParams,
  ProgramData,
} from "@interfaces/Interfaces";
import { useConnectionStatus } from "@hooks/app/useConnectionStatus";
import { useRecoilState, useRecoilValue, useRecoilValueLoadable } from "recoil";
import { learningLoopDeeplinkDataAtom, parapassTimelineDataAtom } from "@state/TimelineState";
import { userAtom } from "@state/State";
import * as DatabaseService from "@controllers/DatabaseService";
import { blobToBase64 } from "@utils/parapassUtils";
import { format } from "date-fns";
import _ from "lodash";
import * as FirebaseService from "@controllers/FirebaseService";
import { EventRegister } from "react-native-event-listeners";
import { HeaderTitle } from "parafolio-components";
import ContentContainer from "@components/common/ContentContainer";
import DataController from "@controllers/DataController";
import { Capacitor } from "@capacitor/core";
import classNames from "classnames";
import { TRUST_BACKGROUND_COLOUR_VARIANTS } from "src/Constants";

type Props = {
  visible: boolean;
  closeModal: () => void;
};

const LearningLoopModal: React.FC<Props> = (props) => {
  const { programsWithLearningLoop, getCompetenceWithLearningLoop, getProgressCheckWithLearningLoop } =
    useLearningLoop();
  const connectionStatus = useConnectionStatus();

  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const [parapassTimelineData, setParapassTimelineData] = useRecoilState(parapassTimelineDataAtom);
  const learningLoopDeeplinkData = useRecoilValue(learningLoopDeeplinkDataAtom);

  const handlePersonalEvidence = async () => {
    if (parapassTimelineData) {
      let object: INewTimelineEvidence | object = {};
      const item = parapassTimelineData;
      const timelineReference = `${item.quizSet}-${item.date}`;

      const totalScore = item.score.correct + item.score.incorrect + item.score.unanswered;

      const params: ParapassPDFRequestParams = {
        contactID: user.contents.userData.contactID,
        token: user.contents.token,
        date: format(new Date(item.date), "do MMMM yyyy"),
        quizName: item.title,
        correctCount: `${item.score.correct}`,
        questionCount: `${totalScore}`,
        userName: user.contents.userData.userName,
        quizType: item.type,
      };

      let pdfString;
      let pdf;

      try {
        if (connectionStatus?.connected) {
          pdf = await DatabaseService.getParapassCertificate(params);
          pdfString = await blobToBase64(pdf);
        }
      } catch (error) {
        console.error(error);
      }

      object = _.extend(object, {
        title: item.title,
        date: new Date(),
        activityDate: new Date(item.sortableDate!),
        activityDescription: `Completed ParaPass "${item.title}" quiz and scored ${item.score.correct}/${totalScore} on ${format(item.sortableDate!, "dd MMM yyyy")}`,
        timelineReference,
        pdfData: {
          pdf: pdfString,
          size: pdf?.size,
          certificateFileName: `ParaPass_Certificate_${format(new Date(item.date), "dd-MMM-yyyy_hh-mm-ss")}.pdf`,
        },
      });

      FirebaseService.logEvent("add_timeline_evidence_pressed", {
        type: "ParaPass",
        quizID: item.quizSet,
        title: item.title,
      });

      EventRegister.emit("evidence-modal/add-evidence-timeline", object);
      closeModal();
    } else if (learningLoopDeeplinkData) {
      EventRegister.emit("evidence-modal/add-evidence-deeplink", learningLoopDeeplinkData);
      closeModal();
    }
  };

  const handleProgram = async (program: ProgramData) => {
    if (parapassTimelineData) {
      const progressCheck = getProgressCheckWithLearningLoop(program);
      const competence = getCompetenceWithLearningLoop(program);

      let object: INewTimelineEvidence | object = {};
      const item = parapassTimelineData;
      const timelineReference = `${item.quizSet}-${item.date}`;

      const totalScore = item.score.correct + item.score.incorrect + item.score.unanswered;

      const params: ParapassPDFRequestParams = {
        contactID: user.contents.userData.contactID,
        token: user.contents.token,
        date: format(new Date(item.date), "do MMMM yyyy"),
        quizName: item.title,
        correctCount: `${item.score.correct}`,
        questionCount: `${totalScore}`,
        userName: user.contents.userData.userName,
        quizType: item.type,
      };

      let pdfString;
      let pdf;

      try {
        if (connectionStatus?.connected) {
          pdf = await DatabaseService.getParapassCertificate(params);
          pdfString = await blobToBase64(pdf);
        }
      } catch (error) {
        console.error(error);
      }

      object = _.extend(object, {
        title: item.title,
        date: new Date(item.date),
        activityDate: new Date(item.sortableDate!),
        description: `Completed ParaPass "${item.title}" quiz and scored ${item.score.correct}/${totalScore} on ${format(item.sortableDate!, "dd MMM yyyy")}`,
        timelineReference,
        ...(program && { programID: program.ID }),
        ...(progressCheck && { progressCheckID: progressCheck.ID }),
        ...(competence && { progressCheckCompetenceID: competence.ID }),
        attachments: {
          pdf: pdfString,
          size: pdf?.size,
          certificateFileName: `ParaPass_Certificate_${format(new Date(item.date), "dd-MMM-yyyy_hh-mm-ss")}.pdf`,
        },
      });

      FirebaseService.logEvent("add_timeline_evidence_pressed", {
        type: "ParaPass",
        quizID: item.quizSet,
        title: item.title,
      });

      EventRegister.emit("evidence-modal/add-learning-loop-deeplink", object);
      closeModal();
    } else if (learningLoopDeeplinkData) {
      const progressCheck = getProgressCheckWithLearningLoop(program);
      const competence = getCompetenceWithLearningLoop(program);

      const object: INewDeepLinkLearningLoop | object = {
        ...learningLoopDeeplinkData,
        date: learningLoopDeeplinkData.activityDate,
        description: learningLoopDeeplinkData.activityDescription,
        ...(program && { programID: program.ID }),
        ...(progressCheck && { progressCheckID: progressCheck.ID }),
        ...(competence && { progressCheckCompetenceID: competence.ID }),
      };

      EventRegister.emit("evidence-modal/add-learning-loop-deeplink", object);
      closeModal();
    }
  };

  const closeModal = () => {
    setParapassTimelineData(null);
    props.closeModal();
  };

  const competencesWithLearningLoop = useMemo(() => {
    const competences = programsWithLearningLoop.map((program) => {
      const competence = getCompetenceWithLearningLoop(program);
      return {
        competence: competence,
        program: program,
      };
    });
    const filteredCompetences = competences.filter((competence) => competence.competence !== undefined);

    return filteredCompetences.sort((a, b) => (a.program.Title ?? "").localeCompare(b.program.Title ?? ""));
  }, [programsWithLearningLoop]);

  return (
    <IonModal
      id="learning-loop-modal"
      isOpen={props.visible}
      backdropDismiss
      canDismiss
      onDidDismiss={closeModal}
      className="fullscreen"
    >
      <IonHeader
        style={{
          height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
        }}
      >
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{ maxWidth: DataController.isWeb() ? 980 : undefined }}
        >
          <IonButtons slot="start">
            <IonButton
              mode="ios"
              className="cancelButton"
              onClick={closeModal}
            >
              {"Close"}
            </IonButton>
          </IonButtons>
          <HeaderTitle>{"Add Evidence"}</HeaderTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <ContentContainer className="!pt-3 pb-32">
          <div className="">
            {competencesWithLearningLoop.map((competence) => (
              <div key={`${competence.program.ID}-${competence.competence?.ID}`}>
                <button
                  id={`${competence.program.ID}-${competence.competence?.ID}`}
                  className="flex w-full items-center py-4 active:opacity-70"
                  onClick={() => handleProgram(competence.program)}
                >
                  <div
                    className={classNames(
                      "flex shrink-0 items-center justify-center rounded px-1 py-0.5 text-13px font-semibold text-white",
                      TRUST_BACKGROUND_COLOUR_VARIANTS[competence.program.TrustName!] ?? "!bg-NEAS"
                    )}
                  >
                    {competence.program.TrustName ?? "XXX"}
                  </div>
                  <div className="ml-3 min-w-0 text-left text-16px font-semibold text-cta-blue">
                    {competence.program.Title}
                    {competence.competence?.Name ? ` - ${competence.competence.Name}` : ""}
                  </div>
                </button>
                <div className="h-px w-full bg-grey-30" />
              </div>
            ))}
            <button
              className="flex w-full items-center px-4 py-3 active:opacity-70"
              onClick={handlePersonalEvidence}
            >
              <div className="text-16px font-semibold text-cta-blue">{"Personal evidence"}</div>
            </button>
          </div>
        </ContentContainer>
      </IonContent>
    </IonModal>
  );
};

export default LearningLoopModal;
